import React, { Component } from 'react';
import _ from 'lodash';
import { Modal, Table, Spin, Tooltip, Icon } from 'grab-ui';
import { Grid, Header } from 'semantic-grab-ui';
import { printNumber } from './utils';
import './NotSentDetailsModal.scss';

interface NotSentDetail {
  deliveryDetail: string;
  messageCount: number;
}

interface GroupData {
  name: string;
  details: NotSentDetail[];
}

interface NotSentDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  notSentData: GroupData[];
  selectedChannel: string;
  isLoading?: boolean;
}

class NotSentDetailsModal extends Component<NotSentDetailsModalProps> {
  // Format delivery detail text to be more readable
  formatDeliveryDetail = (detail: string): string => {
    if (!detail) return detail;

    // Capitalize first letter
    let formatted = detail.charAt(0).toUpperCase() + detail.slice(1);

    // Fix common patterns
    formatted = formatted
      // Capitalize after ". "
      .replace(/\.\s+(\w)/g, (match, letter) => `. ${letter.toUpperCase()}`)
      // Capitalize after ": "
      .replace(/:\s+(\w)/g, (match, letter) => `: ${letter.toUpperCase()}`)
      // Fix "consent: false" to "Consent: False"
      .replace(/consent:\s*false/gi, 'Consent: False')
      .replace(/consent:\s*true/gi, 'Consent: True')
      // Capitalize words like "device", "info", "token" when they start a phrase
      .replace(/\b(device|info|token|push|inbox|email|users|entity|requested)\b/gi,
        (match) => match.charAt(0).toUpperCase() + match.slice(1).toLowerCase());

    return formatted;
  };

  // Aggregate not-sent details across all groups
  aggregateDetails = (notSentData: GroupData[]): NotSentDetail[] => {
    const aggregatedMap: Record<string, number> = {};

    _.forEach(notSentData, (groupData) => {
      const details = _.get(groupData, 'details', []);
      _.forEach(details, (detail) => {
        const deliveryDetail = detail.deliveryDetail;
        const messageCount = detail.messageCount || 0;

        // Skip "sent" entries
        if (deliveryDetail === 'sent') {
          return;
        }

        if (aggregatedMap[deliveryDetail]) {
          aggregatedMap[deliveryDetail] += messageCount;
        } else {
          aggregatedMap[deliveryDetail] = messageCount;
        }
      });
    });

    // Convert to array and sort by message count (descending)
    const aggregatedArray = _.map(aggregatedMap, (count, reason) => ({
      deliveryDetail: reason,
      messageCount: count,
    }));

    return _.orderBy(aggregatedArray, ['messageCount'], ['desc']);
  };

  // Get details grouped by group name
  getDetailsPerGroup = (notSentData: GroupData[]) => {
    return _.map(notSentData, (groupData) => {
      // Filter out "sent" entries
      const filteredDetails = _.filter(groupData.details, detail => detail.deliveryDetail !== 'sent');
      return {
        name: groupData.name,
        details: _.orderBy(filteredDetails, ['messageCount'], ['desc']),
        total: _.sumBy(filteredDetails, 'messageCount'),
      };
    });
  };

  render() {
    const { visible, onClose, notSentData, selectedChannel, isLoading } = this.props;

    const aggregatedDetails = !_.isEmpty(notSentData) ? this.aggregateDetails(notSentData) : [];
    const detailsPerGroup = !_.isEmpty(notSentData) ? this.getDetailsPerGroup(notSentData) : [];
    const totalNotSent = _.sumBy(aggregatedDetails, 'messageCount');

    const columns = [
      {
        title: 'Status',
        dataIndex: 'deliveryDetail',
        key: 'deliveryDetail',
        width: '60%',
        render: (detail: string) => this.formatDeliveryDetail(detail),
      },
      {
        title: 'Messages',
        dataIndex: 'messageCount',
        key: 'messageCount',
        width: '40%',
        render: (count) => printNumber(count),
      },
    ];

    return (
      <Modal
        visible={visible}
        onCancel={onClose}
        title={`Not Sent Details`}
        width={900}
        footer={null}
        className="not-sent-details-modal"
        style={{ marginTop: 100 }}
      >
        <div className="modal-content">
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" tip="Loading not sent details..." />
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <Header as="h3" style={{ marginBottom: 0, marginRight: 8 }}>
                  Overall Summary
                </Header>
                <Tooltip title="These are real-time metrics and may differ slightly from the summary metrics due to data processing delays." placement="right">
                  <Icon type="info-circle" style={{ cursor: 'pointer' }} />
                </Tooltip>
              </div>
              <p style={{ color: '#707070', marginBottom: 16 }}>
                Total Not Sent Messages: <strong>{printNumber(totalNotSent)}</strong>
              </p>

          <Table
            columns={columns}
            dataSource={aggregatedDetails}
            pagination={false}
            size="small"
            rowKey="deliveryDetail"
            style={{ marginBottom: 32 }}
          />

          {detailsPerGroup.length > 1 && (
            <React.Fragment>
              <Header as="h3" style={{ marginTop: 32, marginBottom: 16 }}>
                Breakdown by Group
              </Header>

              {_.map(detailsPerGroup, (group, index) => (
                <div key={index} style={{ marginBottom: 24 }}>
                  <Header as="h4" style={{ marginBottom: 12 }}>
                    {group.name}
                  </Header>
                  <p style={{ color: '#707070', marginBottom: 12 }}>
                    Total: <strong>{printNumber(group.total)}</strong>
                  </p>
                  <Table
                    columns={[
                      {
                        title: 'Status',
                        dataIndex: 'deliveryDetail',
                        key: 'deliveryDetail',
                        width: '60%',
                        render: (detail: string) => this.formatDeliveryDetail(detail),
                      },
                      {
                        title: 'Messages',
                        dataIndex: 'messageCount',
                        key: 'messageCount',
                        width: '40%',
                        render: (count) => printNumber(count),
                      },
                    ]}
                    dataSource={group.details}
                    pagination={false}
                    size="small"
                    rowKey={(record, idx) => `${group.name}-${record.deliveryDetail}-${idx}`}
                  />
                </div>
              ))}
            </React.Fragment>
          )}
            </>
          )}
        </div>
      </Modal>
    );
  }
}

export default NotSentDetailsModal;
